/**
 * PSD → EditorDocument importer. Parses a `.psd` / `.psb` ArrayBuffer
 * via `ag-psd` and walks the layer tree, producing:
 *
 *  - an {@link EditorDocument} the editor can mount
 *  - per-leaf-layer pixel canvases the canvas hook materializes into
 *    Fabric Image objects (positioned by their PSD bounding box)
 *  - a `layerOriginById` map (editor LayerNode.id → PSD layer index)
 *    that `lib/psd-source-stash.ts` persists for the lossless export
 *  - a list of `warnings` describing which PSD features were flattened
 *    to raster — surfaced to the user via a post-import toast
 *
 * Scope of this phase (Tier 2 Group F sub-piece #2, 2a-ii):
 *
 *  - Raster layers → `LayerNode { type: 'image' }`
 *  - Groups → `LayerNode { type: 'group' }`
 *  - Hidden / opacity / blend-mode / name pass-through
 *  - Color mode rejection (RGB only — hard reject everything else)
 *  - Bit depth rejection (8 bpc only — hard reject 16 / 32)
 *
 * Deferred to subsequent phases (but the stash still records origin
 * indices for these so the export path can restore them losslessly):
 *
 *  - Layer masks (raster + vector) — `LayerMask` materialization needs
 *    Fabric-side wiring that lands alongside the canvas-hook update.
 *  - Adjustment layers (matched kinds) — phase 2a-iii.
 *  - Text + vector shape layers — phase 2a-iv.
 *  - Smart objects, layer effects, smart filters, layer comps,
 *    clipping masks — flatten + stash; native preservation only via
 *    export-time stash restoration (PR 2b).
 *
 * No DOM dependencies in the pure traversal (`buildEditorDocument`) so
 * tests can feed synthetic ag-psd `Psd`-shape objects. The byte-level
 * orchestration (`parsePsdToDocument`) dynamic-imports `ag-psd` so
 * non-PSD users don't pay its ~600KB bundle cost on initial load.
 */

import type { Psd, Layer, BlendMode as PsdBlendMode, ColorMode } from 'ag-psd';
import type { BlendMode as EditorBlendMode, EditorDocument, LayerNode, VectorPath } from '../types';
import type { PsdLayerOrigin } from './psd-source-stash';
import { mapPsdAdjustment } from './psd-adjustment-map';
import { parsePsdVector } from './psd-vector-import';
import { parsePsdText } from './psd-text-import';
import type { ParsedSvgTextLayer } from './svg-text-import';

/** One reason a PSD layer was flattened to raster on import (or skipped
 *  entirely). Aggregated by the toast in 2a-vi into a user-readable
 *  summary; per-layer detail is available on a click-to-expand. */
export interface PsdImportWarning {
  /** Editor LayerNode id assigned to the flattened layer, OR null if
   *  the layer was skipped (e.g. no canvas + no recognized
   *  preservable content). */
  layerId: string | null;
  /** PSD layer's display name. */
  layerName: string;
  /** Reason category. Distinct from `message` so the toast can group
   *  ("3 smart-objects flattened") instead of listing every layer. */
  reason:
    | 'adjustment-layer'
    | 'adjustment-lossy'
    | 'text-layer'
    | 'vector-layer'
    | 'smart-object'
    | 'layer-effects'
    | 'layer-mask-deferred'
    | 'vector-mask-deferred'
    | 'unmapped-blend-mode'
    | 'no-pixels';
  /** Free-form detail for the click-to-expand UI. */
  message?: string;
}

/** Raster content for one editor LayerNode, ready for the canvas hook
 *  to add as a Fabric.Image. */
export interface PsdLayerImage {
  /** The PSD layer's rendered pixels. Originates from
   *  `ag-psd.Layer.canvas` — already premultiplied / un-RLE'd. */
  canvas: HTMLCanvasElement;
  /** Bounding-box origin in PSD canvas coordinates. Width / height
   *  come from the canvas itself. */
  left: number;
  top: number;
}

export interface PsdImportResult {
  document: EditorDocument;
  layerImages: Map<string, PsdLayerImage>;
  /** Vector paths indexed by editor `LayerNode.id` for vector
   *  layers materialized from PSD's `LayerVectorMask` chunks
   *  (PSD natives follow-up, item 2a-iv). Caller (EditorPage's
   *  `loadPsdFile`) materializes Fabric Path objects via
   *  `canvas.addVectorObject(layerId, path)`. */
  vectorPaths: Map<string, VectorPath>;
  /** Text descriptors indexed by editor `LayerNode.id` for text
   *  layers materialized from PSD's `LayerTextData` chunks. Caller
   *  materializes IText objects via `canvas.addTextLayer`. Shape
   *  mirrors `svg-text-import.ParsedSvgTextLayer` so the existing
   *  canvas-hook helper handles both PSD and SVG text natively. */
  textDescriptors: Map<string, ParsedSvgTextLayer>;
  layerOriginById: Record<string, PsdLayerOrigin>;
  warnings: PsdImportWarning[];
  originalWidth: number;
  originalHeight: number;
}

/** A user-readable summary of an import's warnings, grouped by
 *  reason category. Built by `summarizePsdWarnings` for the toast UI
 *  — the toast surfaces the `title` + `description`; the per-reason
 *  detail is available for a click-to-expand view (not yet built). */
export interface PsdWarningSummary {
  /** Total flatten / skip events across all categories. */
  totalCount: number;
  /** Per-reason counts, in display order. Zero-count reasons are
   *  omitted so a "0 smart-objects flattened" line never renders. */
  byReason: { reason: PsdImportWarning['reason']; count: number; label: string }[];
  /** Short toast title — e.g. "5 layers flattened" or "Imported with 2 warnings". */
  title: string;
  /** Toast description — comma-separated category breakdown. */
  description: string;
}

const REASON_LABELS: Record<PsdImportWarning['reason'], string> = {
  'adjustment-layer': 'unsupported adjustment',
  'adjustment-lossy': 'simplified adjustment',
  'text-layer': 'text',
  'vector-layer': 'vector',
  'smart-object': 'smart object',
  'layer-effects': 'layer effects',
  'layer-mask-deferred': 'layer mask',
  'vector-mask-deferred': 'vector mask',
  'unmapped-blend-mode': 'unsupported blend mode',
  'no-pixels': 'empty layer',
};

/** Pluralize an English label given a count — naive ("layer" → "layers"),
 *  good enough for category names. Trailing "s" already? Leave alone. */
function pluralize(label: string, count: number): string {
  if (count === 1) return label;
  if (label.endsWith('s')) return label;
  return `${label}s`;
}

/** Group raw warnings into a render-ready summary. Pure — no DOM,
 *  no toast deps. Display order follows `REASON_LABELS` insertion
 *  order so the toast text is deterministic for tests. */
export function summarizePsdWarnings(warnings: PsdImportWarning[]): PsdWarningSummary | null {
  if (warnings.length === 0) return null;
  const counts = new Map<PsdImportWarning['reason'], number>();
  for (const w of warnings) {
    counts.set(w.reason, (counts.get(w.reason) ?? 0) + 1);
  }
  const byReason = (Object.keys(REASON_LABELS) as PsdImportWarning['reason'][])
    .filter((r) => counts.has(r))
    .map((r) => {
      const count = counts.get(r)!;
      return { reason: r, count, label: REASON_LABELS[r] };
    });
  const totalCount = warnings.length;
  const title = `Imported with ${totalCount} ${pluralize('warning', totalCount)}`;
  const description = byReason
    .map(({ count, label }) => `${count} ${pluralize(label, count)}`)
    .join(', ');
  return { totalCount, byReason, title, description };
}

/** Human-readable name for an ag-psd color-mode integer. ag-psd
 *  declares these as a `const enum` which doesn't survive at runtime
 *  for reverse lookup; this map mirrors the enum for error messages
 *  and warnings. */
const COLOR_MODE_NAMES: Record<number, string> = {
  0: 'Bitmap',
  1: 'Grayscale',
  2: 'Indexed',
  3: 'RGB',
  4: 'CMYK',
  7: 'Multichannel',
  8: 'Duotone',
  9: 'Lab',
};

function colorModeName(mode: number | undefined): string {
  if (mode === undefined) return 'unknown';
  return COLOR_MODE_NAMES[mode] ?? `unknown (${mode})`;
}

/** Color modes that ag-psd's canvas decoder converts losslessly to
 *  RGBA on read — they import without precision concerns:
 *
 *    - Bitmap (1-bit): 0 → black, 1 → white, replicated to RGB
 *    - Grayscale (8-bit): single channel replicated to R = G = B
 *    - Indexed (8-bit): palette lookup → RGBA
 *    - RGB: direct
 *
 *  Modes NOT in this set (CMYK, Lab, Duotone, Multichannel) require
 *  gamut / channel conversions that aren't lossless and would
 *  silently change the image's color. We reject them with a clear
 *  error so the user can convert in their source tool and decide
 *  the tradeoff themselves. */
const LOSSLESS_RGB_COMPATIBLE_MODES = new Set<number>([
  0 /* Bitmap */, 1 /* Grayscale */, 2 /* Indexed */, 3 /* RGB */,
]);

/** Thrown when the PSD's color mode is one that can't be losslessly
 *  imported (CMYK / Lab / Duotone / Multichannel). Auto-conversion
 *  is a precision-loss footgun that masquerades as support; we
 *  reject hard with a clear error and let the user convert in their
 *  source tool. */
export class PsdColorModeError extends Error {
  constructor(public readonly colorMode: ColorMode) {
    const name = colorModeName(colorMode);
    super(
      `${name} PSDs are not supported (only RGB, Grayscale, Indexed, and Bitmap modes import). Please convert to RGB in your source tool and try again.`,
    );
    this.name = 'PsdColorModeError';
  }
}

/** Thrown when the PSD's bit depth isn't 8 bits per channel. Same
 *  rationale as the color-mode rejection — half-supporting 16/32 bpc
 *  is worse than refusing. */
export class PsdBitDepthError extends Error {
  constructor(public readonly bitsPerChannel: number) {
    super(
      `Only 8-bit PSDs are supported. This file is ${bitsPerChannel}-bit — please convert to 8 bits per channel and try again.`,
    );
    this.name = 'PsdBitDepthError';
  }
}

/** ag-psd uses Photoshop's blend-mode strings (lowercase, space-
 *  separated); the editor uses CSS-Compositing names. The mapping is
 *  mostly 1:1 because the editor's `BlendMode` already aligns with
 *  the CSS spec.
 *
 *  ag-psd's `'pass through'` is the group-only "no isolation" mode —
 *  we don't yet model it; map to Normal and emit a warning. The PSD
 *  stash preserves the original so the export path can restore it.
 *
 *  Unmapped entries fall back to `'source-over'` with a warning so
 *  the editor displays something rather than crashing the import. */
const BLEND_MODE_MAP: Record<string, EditorBlendMode> = {
  'pass through': 'source-over',
  normal: 'source-over',
  multiply: 'multiply',
  screen: 'screen',
  overlay: 'overlay',
  darken: 'darken',
  lighten: 'lighten',
  'color dodge': 'color-dodge',
  'color burn': 'color-burn',
  'hard light': 'hard-light',
  'soft light': 'soft-light',
  difference: 'difference',
  exclusion: 'exclusion',
  hue: 'hue',
  saturation: 'saturation',
  color: 'color',
  luminosity: 'luminosity',
};

/** Map a PSD blend mode to the editor's. Returns the mapped value and
 *  a flag indicating whether the mode required fallback (caller emits
 *  a warning for the latter). */
export function mapBlendMode(psdBlendMode: PsdBlendMode | undefined): {
  blendMode: EditorBlendMode;
  fallback: boolean;
} {
  if (!psdBlendMode) return { blendMode: 'source-over', fallback: false };
  const mapped = BLEND_MODE_MAP[psdBlendMode];
  if (mapped) return { blendMode: mapped, fallback: psdBlendMode === 'pass through' };
  return { blendMode: 'source-over', fallback: true };
}

/** ag-psd's `Layer.opacity` is **already 0–1 normalized** (per the
 *  library's API — its writer scales by 255 when serializing to the
 *  PSD wire format). The editor also uses 0–1, so this is a clamp,
 *  not a unit conversion. Pass-through `undefined` retains the
 *  editor's "no explicit opacity" semantic.
 *
 *  Verified by the writePsd round-trip spike (`psd-writepsd-spike.test.ts`)
 *  + the ag-psd writer source (psdWriter.js: `Math.round(clamp(
 *  layer.opacity ?? 1, 0, 1) * 255)`). */
function mapOpacity(psdOpacity: number | undefined): number | undefined {
  if (psdOpacity === undefined) return undefined;
  return Math.max(0, Math.min(1, psdOpacity));
}

/** Walk a parsed ag-psd `Psd` and build an editor document. Pure — no
 *  DOM dependencies. Use `parsePsdToDocument` for the bytes-in path.
 *
 *  Throws on unsupported color mode / bit depth. */
export function buildEditorDocument(psd: Psd, nextLayerId: () => string): PsdImportResult {
  if (psd.colorMode !== undefined && !LOSSLESS_RGB_COMPATIBLE_MODES.has(psd.colorMode)) {
    throw new PsdColorModeError(psd.colorMode);
  }
  if (psd.bitsPerChannel !== undefined && psd.bitsPerChannel !== 8) {
    throw new PsdBitDepthError(psd.bitsPerChannel);
  }

  const layers: Record<string, LayerNode> = {};
  const layerImages = new Map<string, PsdLayerImage>();
  const vectorPaths = new Map<string, VectorPath>();
  const textDescriptors = new Map<string, ParsedSvgTextLayer>();
  const layerOriginById: Record<string, PsdLayerOrigin> = {};
  const warnings: PsdImportWarning[] = [];

  // Depth-first counter — every PSD layer gets a stable index that
  // identifies it for the stash on export. The counter increments in
  // visit order, NOT in original PSD-file-order order, but that's
  // fine because we use it as a pointer back into the re-parsed PSD
  // tree at export time (and the re-parsed tree walks in the same
  // visit order).
  let psdIndex = 0;

  function visit(psdLayer: Layer, parentId: string | null): string | null {
    const editorId = nextLayerId();
    const myIndex = psdIndex++;
    layerOriginById[editorId] = { psdLayerIndex: myIndex };

    const name = psdLayer.name ?? '';
    const isGroup = Array.isArray(psdLayer.children);

    // Blend-mode mapping — emit a warning if the mode isn't natively
    // representable. Stash preserves the original; user sees the
    // closest CSS-compositing equivalent in the editor.
    const { blendMode, fallback: bmFallback } = mapBlendMode(psdLayer.blendMode);
    if (bmFallback) {
      warnings.push({
        layerId: editorId,
        layerName: name,
        reason: 'unmapped-blend-mode',
        message: `PSD blend mode "${psdLayer.blendMode}" not natively supported; rendered as Normal (original preserved in stash for export).`,
      });
    }

    if (isGroup) {
      // Group node — recurse children first, then synthesize the
      // group LayerNode with the resolved childIds. ag-psd's
      // `children` is in PSD file order (bottom-to-top); the editor
      // stores `childIds` top-first. Reverse after walking so the
      // visit order (and `psdLayerIndex` assignment) stays aligned
      // with the original PSD's serialization, but the editor sees
      // top-first stacking.
      const children = psdLayer.children ?? [];
      const childIds: string[] = [];
      for (const child of children) {
        const childId = visit(child, editorId);
        if (childId) childIds.push(childId);
      }
      layers[editorId] = {
        id: editorId,
        name: name || 'Group',
        type: 'group',
        visible: !psdLayer.hidden,
        opacity: mapOpacity(psdLayer.opacity),
        blendMode,
        parentId,
        childIds: childIds.slice().reverse(),
      };
      return editorId;
    }

    // Leaf layer — classify by which optional PSD chunks are present.
    // Adjustment layers go through their own mapper (`mapPsdAdjustment`)
    // which returns an editor `AdjustmentConfig` for matched kinds; the
    // resulting LayerNode is `type: 'adjustment'` (no raster pixels).
    // Unmatched kinds fall back to the raster flatten path with a
    // warning; the stash preserves the original PSD chunk so the
    // export path can restore it.
    if (psdLayer.adjustment) {
      const mapped = mapPsdAdjustment(psdLayer.adjustment);
      if (mapped) {
        layers[editorId] = {
          id: editorId,
          name: name || 'Adjustment',
          type: 'adjustment',
          visible: !psdLayer.hidden,
          opacity: mapOpacity(psdLayer.opacity),
          blendMode,
          parentId,
          adjustment: mapped.config,
        };
        if (mapped.lossy) {
          warnings.push({
            layerId: editorId,
            layerName: name,
            reason: 'adjustment-lossy',
            message: `Adjustment "${name}" (${psdLayer.adjustment.type}) imported with a simplified configuration — original preserved in stash for export.`,
          });
        }
        return editorId;
      }
      // Unmatched adjustment kind — fall through to the raster path
      // below so we still display SOMETHING, with a warning.
      warnings.push({
        layerId: editorId,
        layerName: name,
        reason: 'adjustment-layer',
        message: `Adjustment "${name}" (${psdLayer.adjustment.type}) rendered as raster (kind not yet supported natively; original preserved in stash for export).`,
      });
    }
    // Text layer — try native first (PSD natives follow-up, item
    // 2a-iv). When the layer's text record has no warp / textPath
    // / grid AND the transform is pure scale+translate, emit as
    // a native text LayerNode + textDescriptors entry.
    if (psdLayer.text) {
      const textResult = parsePsdText(psdLayer);
      if (textResult.ok) {
        // PSD allows a single layer to carry BOTH a text record
        // AND a vector mask (shaped text containers). The editor
        // doesn't model that combination; emitting a text-type
        // LayerNode discards the vector mask. Stash preserves
        // the original. Surface a warning so users know the
        // outline isn't visible in-editor. (Pre-PR review R1
        // Critical.)
        if (psdLayer.vectorMask) {
          warnings.push({
            layerId: editorId,
            layerName: name,
            reason: 'text-layer',
            message: `Text layer "${name}" has a vector outline that the editor can't display alongside text content; the outline is preserved in the stash for round-trip export.`,
          });
        }
        layers[editorId] = {
          id: editorId,
          name: name || 'Text',
          type: 'text',
          visible: !psdLayer.hidden,
          opacity: mapOpacity(psdLayer.opacity),
          blendMode,
          parentId,
        };
        textDescriptors.set(editorId, textResult.value);
        return editorId;
      }
      // Native parse rejected (warp / textPath / grid / rotated
      // transform / etc.) — fall through to raster. Stash
      // preserves the original.
      warnings.push({
        layerId: editorId,
        layerName: name,
        reason: 'text-layer',
        message: `Text layer "${name}" rendered as raster (${textResult.reason}; original preserved in stash for export).`,
      });
    }
    if (psdLayer.placedLayer) {
      warnings.push({
        layerId: editorId,
        layerName: name,
        reason: 'smart-object',
        message: `Smart object "${name}" rendered as raster (preserved in stash for export).`,
      });
    }
    if (psdLayer.effects) {
      warnings.push({
        layerId: editorId,
        layerName: name,
        reason: 'layer-effects',
        message: `Layer effects on "${name}" not yet natively rendered (preserved in stash for export).`,
      });
    }
    if (psdLayer.mask) {
      warnings.push({
        layerId: editorId,
        layerName: name,
        reason: 'layer-mask-deferred',
        message: `Mask on "${name}" not yet imported (preserved in stash for export).`,
      });
    }
    // Vector mask — try native first (PSD natives follow-up,
    // item 2a-iv). When the layer carries a vectorMask with one
    // or more BezierPaths AND its fill is `type: 'color'` (or
    // absent), emit as a native `LayerNode { type: 'vector' }`
    // with the path attached. Gradient / pattern fills and
    // unsupported strokes fall back to raster via the existing
    // warning path; the stash preserves the original for export
    // round-trip.
    if (psdLayer.vectorMask) {
      const vectorResult = parsePsdVector(psdLayer);
      if (vectorResult.ok) {
        layers[editorId] = {
          id: editorId,
          name: name || 'Vector',
          type: 'vector',
          visible: !psdLayer.hidden,
          opacity: mapOpacity(psdLayer.opacity),
          blendMode,
          parentId,
          path: vectorResult.value,
        };
        vectorPaths.set(editorId, vectorResult.value);
        return editorId;
      }
      // Native parse rejected — fall through to raster with a
      // descriptive warning that captures the reason. Stash still
      // preserves the original.
      warnings.push({
        layerId: editorId,
        layerName: name,
        reason: 'vector-mask-deferred',
        message: `Vector mask on "${name}" not natively imported (${vectorResult.reason}; original preserved in stash for export).`,
      });
    }

    if (!psdLayer.canvas) {
      warnings.push({
        layerId: null,
        layerName: name,
        reason: 'no-pixels',
        message: `Layer "${name}" has no pixel content; skipped.`,
      });
      // Strip the origin entry we just allocated — the layer didn't
      // make it into the editor, so its index would point at nothing
      // useful.
      delete layerOriginById[editorId];
      return null;
    }

    layers[editorId] = {
      id: editorId,
      name: name || 'Layer',
      type: 'image',
      visible: !psdLayer.hidden,
      opacity: mapOpacity(psdLayer.opacity),
      blendMode,
      parentId,
    };
    layerImages.set(editorId, {
      canvas: psdLayer.canvas,
      left: psdLayer.left ?? 0,
      top: psdLayer.top ?? 0,
    });
    return editorId;
  }

  const rootIds: string[] = [];
  for (const top of psd.children ?? []) {
    const id = visit(top, null);
    if (id) rootIds.push(id);
  }

  // Text-on-path rebinding pass. The export side appends marker
  // tags (`⟪fias:tp-bind=tok⟫` on the text layer, `⟪fias:tp-anchor=tok⟫`
  // on the vector layer it follows) to layer names so the editor-
  // only binding survives the round-trip. Here we parse those
  // markers back into `textPath: { pathLayerId }` on the text
  // LayerNode, then strip the markers from the user-facing names.
  // See `psd-export.ts` `applyTextPathMarker` for the producer.
  rebindTextPathMarkers(layers);

  // Reverse for the same reason as `childIds` above — ag-psd file
  // order is bottom-to-top, editor expects top-first.
  return {
    document: { rootIds: rootIds.slice().reverse(), layers },
    layerImages,
    vectorPaths,
    textDescriptors,
    layerOriginById,
    warnings,
    originalWidth: psd.width,
    originalHeight: psd.height,
  };
}

/** Match `⟪fias:tp-(anchor|bind)=tok⟫` substrings — emitted by the
 *  PSD export's `applyTextPathMarker`. Globals + non-greedy so we
 *  can both extract and strip multiple markers from a single name
 *  (defensive; in practice each layer carries at most one marker). */
const TEXT_PATH_MARKER_PATTERN = /\s*⟪fias:tp-(anchor|bind)=([^⟫]+)⟫/g;

/** Walk all imported LayerNodes, link `textPath` bindings by the
 *  marker tokens left in layer names, then strip the markers so
 *  user-facing names are clean. Mutates the layers map in place. */
function rebindTextPathMarkers(layers: Record<string, LayerNode>): void {
  // First pass: collect (token, role) → editor layer id.
  const anchors = new Map<string, string>(); // token → editor id of path layer
  const binds = new Map<string, string>(); // token → editor id of text layer
  for (const id in layers) {
    const node = layers[id];
    if (!node.name) continue;
    // Reset lastIndex defensively — global regex is stateful.
    TEXT_PATH_MARKER_PATTERN.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = TEXT_PATH_MARKER_PATTERN.exec(node.name)) !== null) {
      const role = m[1];
      const token = m[2];
      if (role === 'anchor') anchors.set(token, id);
      else if (role === 'bind') binds.set(token, id);
    }
  }
  // Second pass: apply binding for each (text, path) pair and strip
  // markers from every layer's name.
  for (const [token, textId] of binds) {
    const pathId = anchors.get(token);
    if (!pathId) continue;
    const textNode = layers[textId];
    if (textNode && textNode.type === 'text') {
      textNode.textPath = { pathLayerId: pathId };
    }
  }
  for (const id in layers) {
    const node = layers[id];
    if (!node.name) continue;
    TEXT_PATH_MARKER_PATTERN.lastIndex = 0;
    if (TEXT_PATH_MARKER_PATTERN.test(node.name)) {
      // Strip every marker substring. Use a fresh regex copy so the
      // global state doesn't interfere with the replace's match.
      node.name = node.name.replace(/\s*⟪fias:tp-(?:anchor|bind)=[^⟫]+⟫/g, '').trim();
    }
  }
}

/** Bytes-in entry point. Dynamic-imports `ag-psd` so non-PSD users
 *  don't pay the bundle cost on editor initial load. */
export async function parsePsdToDocument(
  arrayBuffer: ArrayBuffer,
  nextLayerId: () => string,
): Promise<PsdImportResult> {
  const { readPsd } = await import('ag-psd');
  const psd = readPsd(arrayBuffer, {
    // Skip the composite-image thumbnail — we never need it; saves
    // memory + parse time for large PSDs.
    skipCompositeImageData: true,
    skipThumbnail: true,
  });
  return buildEditorDocument(psd, nextLayerId);
}
